import { ForwardedRef, Key, forwardRef, useId } from "react";

import {
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  Autocomplete as MUIAutocomplete,
  MenuItem,
} from "@mui/material";
import { gray } from "configs/colors";
import { rem } from "utils";

import { useMultiAutocomplete } from "./MultiAutocomplete.hooks";
import {
  Container,
  Input,
  SelectedIconWrapper,
  StyledPopper,
  Tag,
} from "./MultiAutocomplete.styles";
import {
  MultiAutocompleteProps,
  MultiAutocompleteValue,
  MultiAutocompleteReference,
} from "./MultiAutocomplete.types";
import { CheckIcon, ChevronDownIcon, XCloseIcon, XIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";
import Typography from "../Typography/Typography";

const MultiAutocompleteInner = <V, R>(
  {
    color,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputRef,
    label,
    loading,
    options = [],
    placeholder,
    prefix,
    readOnly,
    required,
    suffix,
    value,
    renderOption,
    renderTag,
    onChange,
    onInputChange,
  }: MultiAutocompleteProps<MultiAutocompleteValue<V>>,
  ref: ForwardedRef<R>
) => {
  const { autocompleteRef, valueState, onChangeHandler } = useMultiAutocomplete(
    { ref, defaultValue, value, onChange }
  );
  const formId = useId();

  return (
    <Container>
      {label && (
        <InputLabel htmlFor={id ?? formId} required={required}>
          {label}
        </InputLabel>
      )}
      <MUIAutocomplete
        multiple
        fullWidth
        disablePortal
        disableCloseOnSelect
        ref={autocompleteRef}
        id={id ?? formId}
        color={color}
        disabled={disabled}
        loading={loading}
        readOnly={readOnly}
        options={options}
        value={valueState}
        popupIcon={<ChevronDownIcon fontSize="small" />}
        clearIcon={<XIcon fontSize="small" />}
        isOptionEqualToValue={(option, v) => option.value === v.value}
        onChange={(_event, selectedValue) =>
          onChangeHandler(
            selectedValue as unknown as MultiAutocompleteValue<V>[] | null
          )
        }
        onInputChange={(_e, v) => onInputChange?.(v)}
        renderOption={(props, option, state) => (
          <MenuItem {...props}>
            {typeof renderOption === "function"
              ? renderOption(props, option, state)
              : option.label}
            {state.selected ? (
              <SelectedIconWrapper>
                <CheckIcon color="primary" fontSize="small" />
              </SelectedIconWrapper>
            ) : null}
          </MenuItem>
        )}
        renderInput={(params) => (
          <Input
            {...params}
            color={color}
            inputRef={inputRef}
            required={required}
            disabled={disabled}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {prefix ? (
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      marginLeft="8px"
                      marginRight="6px"
                    >
                      {prefix}
                    </Box>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={14} />
                  ) : null}
                  {suffix}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(v, getTagProps) =>
          v.map((o, index: number) => {
            const { key, className, onDelete, ...innerProps } = getTagProps({
              index,
            });
            return (
              <Tag
                key={key as unknown as Key}
                className="MultiAutocomplete-Tag"
                {...innerProps}
              >
                {typeof renderTag === "function" ? (
                  renderTag(o)
                ) : (
                  <Typography type="TextSM" fontWeight={500} color="inherit">
                    {o.label}
                  </Typography>
                )}
                {!innerProps.disabled ? (
                  <IconButton
                    disableRipple
                    sx={{
                      padding: "2px",
                      cursor: "pointer",
                      color: gray[400],
                      marginLeft: "3px",
                      flex: "0 0 auto",
                    }}
                    onClick={onDelete}
                  >
                    <XCloseIcon
                      color="inherit"
                      sx={{
                        fontSize: rem(12),
                      }}
                    />
                  </IconButton>
                ) : null}
              </Tag>
            );
          })
        }
        PopperComponent={StyledPopper}
      />
      {helperText && (
        <FormHelperText disabled={disabled} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Container>
  );
};

const MultiAutocomplete = forwardRef(MultiAutocompleteInner) as <V, D>(
  props: MultiAutocompleteProps<MultiAutocompleteValue<V, D>> & {
    ref?: ForwardedRef<
      MultiAutocompleteReference<MultiAutocompleteValue<V, D>>
    >;
  }
) => ReturnType<typeof MultiAutocompleteInner>;

export default MultiAutocomplete;
